import { useEffect, useMemo, useState } from "react";
import { Search, ArrowRight, ThumbsUp, ThumbsDown, Phone } from "lucide-react";
import { business } from "../data/business";

// FAQ — all answers stay general. Specific ages, weights, prices, dollar
// thresholds and SLA promises were stripped because we don't have them in
// writing from the business. The format is "what we know publicly + call
// to confirm specifics" so the page never asserts something we can't back.
//
// `category` is used for the tab filter (#64). We classify on what the
// question is *about*, not what the answer claims, so we can be confident:
//   safety    — wearing PFDs, swim ability, waiver, fitness/weight
//   booking   — when to book, season, weather/cancellation policy, discounts
//   logistics — bathrooms, gear, parking/RV, shuttle, trip length, GoPro
type FaqItem = {
  id: string;
  q: string;
  a: string;
  category: "safety" | "booking" | "logistics";
};

const faqs: FaqItem[] = [
  {
    id: "min-age",
    category: "safety",
    q: "What's the minimum age to raft?",
    a:
      "Age requirements vary between the Middle Ocoee (the classic family run) and the Upper Olympic course (tougher water). Call us at " +
      business.phone +
      " before booking and we'll match the trip to your group.",
  },
  {
    id: "weight-fitness",
    category: "safety",
    q: "Is there a weight or fitness limit?",
    a: "Whitewater rafting carries weight and life-jacket-fit limits set by our outfitter. If you have specific concerns, call ahead — we'll confirm what works for your group.",
  },
  {
    id: "swim",
    category: "safety",
    q: "Do I need to know how to swim?",
    a: "Every paddler wears a Coast Guard-approved life jacket, and your guide leads the safety briefing on shore before launch. You don't need to be a strong swimmer, but you should be comfortable in moving water.",
  },
  {
    id: "trip-length",
    category: "logistics",
    q: "How long do trips last?",
    a: "Plan for the better part of a half-day with check-in, the on-shore briefing, the run itself, and the shuttle back. Exact times vary by the day's dam-release window — confirmed at booking.",
  },
  {
    id: "bathrooms",
    category: "logistics",
    q: "Are there bathrooms on-site?",
    a: "Yes — restrooms and changing rooms at the lodge. Use them before launch; there are no facilities on the river.",
  },
  {
    id: "gopro",
    category: "logistics",
    q: "Can I bring my own GoPro?",
    a: "Mount-only — head or chest. Handhelds tend to end up in the river. If your trip offers a professional photo option, we'll mention it at check-in.",
  },
  {
    id: "season",
    category: "booking",
    q: "When is the rafting season?",
    a: "The river only flows on scheduled dam-release days. Weekends are typical; midweek varies. Check the release schedule before booking, or call us and we'll confirm dates for your group.",
  },
  {
    id: "weather-cancel",
    category: "booking",
    q: "What's your weather / cancellation policy?",
    a: "Trips run rain-or-shine when releases are scheduled. We only cancel for unsafe conditions like lightning. For specific cancellation terms, see the cancellation policy or call us before booking.",
  },
  {
    id: "shuttle",
    category: "logistics",
    q: "Do you offer shuttle from Chattanooga or Atlanta?",
    a: "Most guests drive themselves (about 1 hour from Chattanooga, 2.5 hours from Atlanta). If you have a large group and need transport, call us — we'll see what we can arrange.",
  },
  {
    id: "discounts",
    category: "booking",
    q: "Are there group, military, or kids discounts?",
    a: "Group rates apply automatically for larger parties at checkout. For military, first-responder, and student rates, call us before booking and we'll apply what's available.",
  },
  {
    id: "parking",
    category: "logistics",
    q: "Where do I park? Can I bring an RV?",
    a: "Free on-site parking at the lodge. RV pads are available in the camping area — see Camping & RV for the rates currently published.",
  },
  {
    id: "waiver",
    category: "safety",
    q: "Do I need to sign a waiver?",
    a: "Yes. Every paddler signs a liability waiver before getting on a raft. Minors require a parent or legal guardian's signature.",
  },
];

type Category = FaqItem["category"];
type Tab = "all" | Category;

const tabs: Array<{ id: Tab; label: string }> = [
  { id: "all", label: "All" },
  { id: "safety", label: "Safety" },
  { id: "booking", label: "Booking" },
  { id: "logistics", label: "Logistics" },
];

const VISITED_KEY = "ocoee.faq.visited";
const VOTES_KEY = "ocoee.faq.votes";

type Vote = "up" | "down";
type VoteMap = Record<string, Vote>;

function readVisited(): string[] {
  try {
    const raw = localStorage.getItem(VISITED_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter((v) => typeof v === "string") : [];
  } catch {
    return [];
  }
}

function writeVisited(ids: string[]) {
  try {
    localStorage.setItem(VISITED_KEY, JSON.stringify(ids));
  } catch {
    /* ignore */
  }
}

function readVotes(): VoteMap {
  try {
    const raw = localStorage.getItem(VOTES_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === "object") return parsed as VoteMap;
    return {};
  } catch {
    return {};
  }
}

function writeVotes(votes: VoteMap) {
  try {
    localStorage.setItem(VOTES_KEY, JSON.stringify(votes));
  } catch {
    /* ignore */
  }
}

// Escape regex specials so the search term can't blow up `new RegExp` (#63).
function escapeRegex(s: string) {
  return s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

// Wrap matched substrings in <mark>, preserving the original casing.
function highlight(text: string, term: string): React.ReactNode {
  const t = term.trim();
  if (!t) return text;
  const re = new RegExp(`(${escapeRegex(t)})`, "ig");
  const parts = text.split(re);
  return parts.map((part, i) =>
    re.test(part) && part.toLowerCase() === t.toLowerCase() ? (
      <mark key={i} className="faq-mark">
        {part}
      </mark>
    ) : (
      <span key={i}>{part}</span>
    ),
  );
}

export function Faq() {
  const [query, setQuery] = useState("");
  const [tab, setTab] = useState<Tab>("all");
  const [visited, setVisited] = useState<string[]>([]);
  const [votes, setVotes] = useState<VoteMap>({});

  // FAQPage schema injected via DOM (matches SchemaJsonLd pattern).
  useEffect(() => {
    const data = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: faqs.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    };
    const id = "schema-faq";
    document.head.querySelector(`#${id}`)?.remove();
    const tag = document.createElement("script");
    tag.id = id;
    tag.type = "application/ld+json";
    tag.text = JSON.stringify(data);
    document.head.appendChild(tag);
    return () => {
      tag.remove();
    };
  }, []);

  // Restore visited + votes from localStorage (#67, #68).
  useEffect(() => {
    setVisited(readVisited());
    setVotes(readVotes());
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return faqs.filter((f) => {
      if (tab !== "all" && f.category !== tab) return false;
      if (!q) return true;
      return f.q.toLowerCase().includes(q) || f.a.toLowerCase().includes(q);
    });
  }, [query, tab]);

  const markVisited = (id: string) => {
    setVisited((prev) => {
      if (prev.includes(id)) return prev;
      const next = [...prev, id];
      writeVisited(next);
      return next;
    });
  };

  const castVote = (id: string, vote: Vote) => {
    setVotes((prev) => {
      if (prev[id]) return prev; // already voted — both buttons disabled
      const next = { ...prev, [id]: vote };
      writeVotes(next);
      return next;
    });
  };

  return (
    <section className="faq" aria-label="Frequently asked questions" id="faq">
      <div className="faq-head">
        <span className="eye">FAQ</span>
        <h2 className="h2">Common questions</h2>
        <label className="faq-search">
          <Search size={16} aria-hidden="true" />
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search the FAQ — try 'cancel', 'age', 'parking'…"
            aria-label="Search frequently asked questions"
          />
        </label>

        {/* #64 — category tabs */}
        <div className="faq-tabs" role="tablist" aria-label="FAQ categories">
          {tabs.map((t) => (
            <button
              key={t.id}
              type="button"
              role="tab"
              aria-selected={tab === t.id}
              className={`faq-tab ${tab === t.id ? "is-active" : ""}`}
              onClick={() => setTab(t.id)}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      <ul className="faq-list">
        {filtered.length === 0 && (
          <li className="faq-empty">
            <p>No matches — ask us directly.</p>
            <div className="faq-empty-actions">
              <a href="/contact" className="faq-empty-link">
                Contact us <ArrowRight size={14} aria-hidden="true" />
              </a>
              <a href={`tel:${business.phoneRaw}`} className="faq-empty-link">
                <Phone size={14} aria-hidden="true" /> {business.phone}
              </a>
            </div>
          </li>
        )}
        {filtered.map((f) => {
          const isVisited = visited.includes(f.id);
          const userVote = votes[f.id];
          return (
            <li
              key={f.id}
              className={`faq-item ${isVisited ? "is-visited" : ""}`}
            >
              <details
                onToggle={(e) => {
                  if ((e.currentTarget as HTMLDetailsElement).open) {
                    markVisited(f.id);
                  }
                }}
              >
                <summary className="faq-q">
                  <span>{highlight(f.q, query)}</span>
                  <span className="faq-toggle" aria-hidden="true" />
                </summary>
                <div className="faq-a">
                  <p>{highlight(f.a, query)}</p>
                  <div className="faq-helpful" aria-label="Was this answer helpful?">
                    {userVote ? (
                      <span className="faq-helpful-thanks">Thanks for the feedback.</span>
                    ) : (
                      <>
                        <span className="faq-helpful-label">Helpful?</span>
                        <div className="faq-vote-row">
                          <button
                            type="button"
                            className="faq-vote"
                            aria-label="Yes, this was helpful"
                            onClick={() => castVote(f.id, "up")}
                          >
                            <ThumbsUp size={14} aria-hidden="true" />
                          </button>
                          <button
                            type="button"
                            className="faq-vote"
                            aria-label="No, this wasn't helpful"
                            onClick={() => castVote(f.id, "down")}
                          >
                            <ThumbsDown size={14} aria-hidden="true" />
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </details>
            </li>
          );
        })}
      </ul>

      <p className="faq-cta">
        Still have a question?{" "}
        <a href="/contact">
          Send a message <ArrowRight size={14} aria-hidden="true" />
        </a>
      </p>
    </section>
  );
}
